
import React, { useState, useEffect } from 'react';
import { StorageService } from './services/storageService';
import { UserRole, College, Assessment, AttemptStatus, CandidateAttempt, SystemSettings } from './types';
import Layout from './components/Layout';
import AdminDashboard from './components/AdminDashboard';
import AssessmentSession from './components/AssessmentSession';
import AlertModal from './components/AlertModal';

const App: React.FC = () => {
  const [currentUser, setCurrentUser] = useState(StorageService.getCurrentUser());
  const [view, setView] = useState<'LOADING' | 'LANDING' | 'LOGIN' | 'ONBOARDING' | 'ASSESSMENT' | 'DASHBOARD' | 'RESULT' | 'NOT_FOUND' | 'EXPIRED'>('LOADING');

  // Registration State
  const [name, setName] = useState('');
  const [studentId, setStudentId] = useState('');
  const [collegeId, setCollegeId] = useState('');
  const [selectedAssessmentId, setSelectedAssessmentId] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLinkAutomated, setIsLinkAutomated] = useState(false);

  // Admin Login State
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminPin, setAdminPin] = useState('');
  const [loginError, setLoginError] = useState('');

  // App context
  const [colleges, setColleges] = useState<College[]>([]);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [currentAttempt, setCurrentAttempt] = useState<CandidateAttempt | null>(null);
  const [existingStream, setExistingStream] = useState<MediaStream | null>(null);

  // Global Alert State
  const [alertState, setAlertState] = useState<{ isOpen: boolean; title: string; message: string; type: 'error' | 'success' | 'info' } | null>(null);

  const showAlert = (title: string, message: string, type: 'error' | 'success' | 'info' = 'info') => {
    setAlertState({ isOpen: true, title, message, type });
  };

  useEffect(() => {
    initApp();
  }, []);

  const initApp = async () => {
    setView('LOADING');
    const { colleges: activeColleges, assessments: activeAssessments } = await refreshContext();

    const user = StorageService.getCurrentUser();

    // Check for custom Link
    const path = window.location.pathname.replace(/^\/+|\/+$/g, '');

    // Special route: /recruiter-portal (admin access)
    if (path === 'recruiter-portal') {
      if (user?.role === UserRole.ADMIN) {
        setView('DASHBOARD');
        return;
      } else {
        setShowAdminLogin(true);
        setView('ONBOARDING');
        return;
      }
    }

    if (path && path.length > 0 && path !== 'index.html') {
      try {
        const link = await StorageService.getAccessLink(path);
        if (link) {
          const collegeExists = activeColleges.some(c => c.id === link.collegeId);
          const assessmentExists = activeAssessments.some(a => a.id === link.assessmentId);

          if (!collegeExists || !assessmentExists) {
            setView('EXPIRED');
            return;
          }

          setCollegeId(link.collegeId);
          setSelectedAssessmentId(link.assessmentId);
          setIsLinkAutomated(true);
          // We keep the URL so refreshing preserves the context
        } else {
          // Invalid link - show not found
          setView('NOT_FOUND');
          return;
        }
      } catch (e) {
        console.warn("Invalid link", e);
        setView('NOT_FOUND');
        return;
      }
    }

    if (user) {
      setCurrentUser(user);
      if (user.role === UserRole.ADMIN) {
        setView('DASHBOARD');
      } else if (user.role === UserRole.CANDIDATE && user.id) {
        const att = await StorageService.getAttempt(user.id);
        if (att) {
          setCurrentAttempt(att);
          if (att.status === AttemptStatus.SUBMITTED || att.status === AttemptStatus.ABANDONED) {
            setView('RESULT');
          } else {
            setView('ASSESSMENT');
          }
        } else {
          setView('ONBOARDING');
        }
      } else {
        setView('ONBOARDING');
      }
    } else {
      // No user and no custom link - show landing page
      if (!path || path === '') {
        setView('LANDING');
      } else {
        setView('ONBOARDING');
      }
    }
  };

  const refreshContext = async () => {
    const allColleges = await StorageService.getColleges();
    const allAssessments = await StorageService.getAssessments();
    const systemSettings = await StorageService.getSettings();
    const activeColleges = allColleges.filter(c => c.isEnabled);
    const activeAssessments = allAssessments.filter(a => a.isActive);
    setColleges(activeColleges);
    setAssessments(activeAssessments);
    setSettings(systemSettings);
    return { colleges: activeColleges, assessments: activeAssessments };
  };

  const handleAdminLogin = async () => {
    setLoginError('');
    const success = await StorageService.loginAdmin(adminPin);
    if (success) {
      setCurrentUser(StorageService.getCurrentUser());
      setView('DASHBOARD');
      setShowAdminLogin(false);
      setAdminPin('');
    } else {
      setLoginError('Invalid Recruiter Access PIN');
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!studentId.trim()) newErrors.studentId = 'Student ID / Roll No is required';
    if (!collegeId) newErrors.college = 'Institution is required';
    if (!selectedAssessmentId) newErrors.assessment = 'Select an assessment';

    if (collegeId && studentId.trim()) {
      const college = colleges.find(c => c.id === collegeId);
      if (college?.allowedStudents && college.allowedStudents.length > 0) {
        const isAllowed = college.allowedStudents.some(s => s.id.toLowerCase() === studentId.trim().toLowerCase());
        if (!isAllowed) {
          newErrors.studentId = 'Access Denied';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStartAssessment = async () => {
    if (!validateForm()) return;

    // Check for camera requirement before starting
    const selectedAss = assessments.find(a => a.id === selectedAssessmentId);
    let stream: MediaStream | null = null;

    if (selectedAss?.requireCamera) {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: true });
        setExistingStream(stream);
      } catch (err) {
        showAlert("Camera Required", "Camera access is required for this assessment. Please allow camera access and try again.", "error");
        return;
      }
    }

    try {
      setView('LOADING');

      // Fetch name and department automatically based on Student ID
      let candidateName = 'Unknown Candidate';
      let department = '';
      const college = colleges.find(c => c.id === collegeId);
      if (college?.allowedStudents) {
        const studentObj = college.allowedStudents.find(s => s.id.toLowerCase() === studentId.trim().toLowerCase());
        if (studentObj?.name) candidateName = studentObj.name;
        if (studentObj?.department) department = studentObj.department;
      }

      const newAtt = await StorageService.createAttempt(candidateName, department, studentId, collegeId, selectedAssessmentId);
      setCurrentAttempt(newAtt);
      setCurrentUser(StorageService.getCurrentUser());
      setView('ASSESSMENT');
    } catch (e: any) {
      showAlert("Start Failed", e.message || 'Session creation failed.', "error");
      setView('ONBOARDING');
    }
  };

  const handleLogout = () => {
    StorageService.logout();
    setCurrentUser(null);
    setCurrentAttempt(null);
    setView('LANDING');
    setName('');
    setStudentId('');
    setCollegeId('');
    setSelectedAssessmentId('');
    setErrors({});
    window.history.pushState({}, '', '/');
    refreshContext();
  };

  // Helper to render alert modal globally
  const renderAlert = () => (
    <AlertModal
      isOpen={!!alertState}
      title={alertState?.title || ''}
      message={alertState?.message || ''}
      type={alertState?.type}
      onClose={() => setAlertState(null)}
    />
  );

  if (view === 'LOADING') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="loader"></div>
          <p className="text-gray-400 font-bold uppercase text-[10px] tracking-widest">Secure Cloud Connection...</p>
        </div>
        {renderAlert()}
      </div>
    );
  }

  if (view === 'NOT_FOUND' || view === 'EXPIRED') {
    const isExpired = view === 'EXPIRED';
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center p-6">
          <div className="max-w-md w-full bg-white dark:bg-gray-900 rounded-[2.5rem] shadow-2xl p-8 md:p-12 text-center border-2 border-gray-50 dark:border-gray-800 transition-colors">
            <div className={`w-24 h-24 ${isExpired ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-500' : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-500'} rounded-3xl flex items-center justify-center mx-auto text-5xl mb-6 transition-colors`}>
              {isExpired ? '⏳' : '🔍'}
            </div>
            <h2 className="text-3xl font-black text-gray-900 dark:text-white mb-4 transition-colors">
              {isExpired ? 'Link Expired' : 'Link Not Found'}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mb-8 leading-relaxed transition-colors">
              {isExpired
                ? 'This assessment link is no longer active. The test may have ended, or the institution access has been revoked.'
                : "The assessment link you're trying to access doesn't exist or has been removed. Please check the URL or contact your administrator."}
            </p>
            <button
              onClick={() => {
                window.location.href = '/';
              }}
              className="w-full bg-indigo-600 text-white py-4 rounded-2xl font-bold shadow-lg shadow-indigo-100 dark:shadow-none hover:bg-indigo-700 transition-all hover:scale-105 active:scale-95"
            >
              Go to Home
            </button>
          </div>
        </div>
        {renderAlert()}
      </Layout>
    );
  }

  if (view === 'LANDING') {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-950 dark:to-gray-900">
          <div className="max-w-6xl w-full">
            {/* Hero Section */}
            <div className="text-center mb-16">
              <img src="/maghil-hub-logo.png" className="w-80 h-auto mx-auto mb-10" alt="Maghil Hub Logo" />
              <p className="text-2xl text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600 font-bold mb-4">
                Innovative IT Solutions for Modern Enterprises
              </p>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto transition-colors">
                Leading Technology Partner for Digital Transformation and Software Innovation
              </p>
            </div>

            {/* Mission Statement */}
            <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-xl p-12 mb-12 border border-gray-100 dark:border-gray-800 transition-colors">
              <h2 className="text-3xl font-black text-gray-900 dark:text-white mb-6 text-center">What We Do</h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 text-center max-w-4xl mx-auto leading-relaxed">
                We deliver cutting-edge IT solutions and services, empowering organizations to leverage emerging technologies for digital transformation, operational excellence, and sustainable growth.
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              <div className="bg-white dark:bg-gray-900 p-10 rounded-3xl shadow-lg border border-gray-100 dark:border-gray-800 transition-all hover:shadow-2xl hover:scale-105">
                <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <span className="text-4xl">💻</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center">Software Development</h3>
                <p className="text-gray-600 dark:text-gray-400 text-center leading-relaxed">
                  Custom software solutions, web and mobile applications built with modern frameworks and best practices for scalability and performance.
                </p>
              </div>

              <div className="bg-white dark:bg-gray-900 p-10 rounded-3xl shadow-lg border border-gray-100 dark:border-gray-800 transition-all hover:shadow-2xl hover:scale-105">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <span className="text-4xl">☁️</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center">Cloud Solutions</h3>
                <p className="text-gray-600 dark:text-gray-400 text-center leading-relaxed">
                  Enterprise cloud infrastructure, migration services, and DevOps automation to optimize costs and enhance operational efficiency.
                </p>
              </div>

              <div className="bg-white dark:bg-gray-900 p-10 rounded-3xl shadow-lg border border-gray-100 dark:border-gray-800 transition-all hover:shadow-2xl hover:scale-105">
                <div className="w-20 h-20 bg-gradient-to-br from-pink-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <span className="text-4xl">🤖</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 text-center">AI & Machine Learning</h3>
                <p className="text-gray-600 dark:text-gray-400 text-center leading-relaxed">
                  Intelligent automation, predictive analytics, and ML-powered solutions to drive data-driven insights and business innovation.
                </p>
              </div>
            </div>
          </div>
        </div>
        {renderAlert()}
      </Layout>
    );
  }

  if (view === 'DASHBOARD' && currentUser?.role === UserRole.ADMIN) {
    // Note: Dashboard might need its own alert handling if it uses alert(), but for now we focus on App.tsx level
    return <Layout showNav onLogout={handleLogout} userName={currentUser?.name} department={currentUser?.department}><AdminDashboard />{renderAlert()}</Layout>;
  }

  if (view === 'ASSESSMENT' && currentAttempt) {
    return <Layout userName={currentUser?.name} department={currentUser?.department}><AssessmentSession attemptId={currentAttempt.id} existingStream={existingStream} onFinish={() => setView('RESULT')} />{renderAlert()}</Layout>;
  }

  const selectedAssessment = assessments.find(a => a.id === selectedAssessmentId);

  if (view === 'RESULT' && currentAttempt) {
    const isFailed = currentAttempt.status === AttemptStatus.ABANDONED;
    return (
      <Layout userName={currentUser?.name} department={currentUser?.department}>
        <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-950 dark:to-gray-900 transition-colors">
          <div className="max-w-md w-full bg-white dark:bg-gray-900 rounded-[2.5rem] shadow-2xl p-8 md:p-12 text-center border border-gray-100 dark:border-gray-800 transform transition-all">
            {isFailed ? (
              <div className="space-y-6">
                <div className="w-24 h-24 bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-500 rounded-[2rem] flex items-center justify-center mx-auto shadow-lg border border-red-50 dark:border-red-900/30">
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h2 className="text-3xl font-black text-gray-900 dark:text-white transition-colors">Session Terminated</h2>
                <p className="text-gray-500 dark:text-gray-400 font-medium leading-relaxed">
                  The session was flagged due to multiple security violations. Please contact the administrator for further details.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                <h2 className="text-3xl font-black text-gray-900 dark:text-white transition-colors uppercase tracking-tight">Well Done!</h2>
                <div className="py-2">
                  <p className="text-gray-500 dark:text-gray-400 font-medium text-lg mb-2 transition-colors">Assessment Submitted Successfully</p>
                  <p className="text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-widest bg-indigo-50 dark:bg-indigo-900/30 inline-block px-4 py-1 rounded-full">Record Snapshotted</p>
                </div>
                <p className="text-gray-400 dark:text-gray-500 text-sm leading-relaxed px-4 transition-colors">
                  Thank you for participating. Your responses have been stored and forwarded to the recruitment team.
                </p>
              </div>
            )}
            <button
              onClick={handleLogout}
              className="mt-10 w-full bg-gray-900 dark:bg-white dark:text-gray-900 text-white py-5 rounded-[1.5rem] font-bold text-lg shadow-xl shadow-gray-200 dark:shadow-none hover:bg-black dark:hover:bg-gray-100 transition-all active:scale-[0.98] border border-gray-900 dark:border-white"
            >
              Close Session
            </button>
          </div>
        </div>
        {renderAlert()}
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex flex-col min-h-[90vh] items-center justify-center p-6">
        <div className="max-w-5xl w-full grid grid-cols-1 lg:grid-cols-2 bg-white dark:bg-gray-900 rounded-[3rem] shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-800 transition-colors">
          <div className="relative p-8 md:p-16 flex flex-col justify-center overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-slate-100 dark:from-gray-800 dark:to-gray-900 transition-colors relative">
            {/* Geometric Pattern Overlay - Darker for contrast on light bg */}
            <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.1]" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, black 1px, transparent 0)`,
              backgroundSize: '24px 24px'
            }}></div>

            <div className="relative z-10">
              {/* Logo Logic */}
              {selectedAssessment?.logoUrl ? (
                <img src={selectedAssessment.logoUrl} className="w-20 h-20 rounded-2xl bg-white p-2 mb-10 object-contain shadow-lg" alt="Logo" />
              ) : (
                <img src="/maghil-hub-logo.png" className="w-72 h-auto mb-10 mix-blend-multiply dark:mix-blend-normal" alt="Maghil Hub" />
              )}
              <h1 className="text-4xl font-black mb-6 leading-tight text-slate-900 dark:text-white transition-colors">
                <span>{selectedAssessment?.name || 'Maghil Hub Internship Hiring Drive'}</span>
              </h1>
              <p className="text-slate-600 dark:text-gray-300 text-lg mb-8 whitespace-pre-wrap font-medium leading-relaxed">
                <span>{selectedAssessment?.welcomeText || 'Enter your details to begin your assessment.'}</span>
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-4 bg-white/60 dark:bg-gray-700/50 p-4 rounded-2xl border border-white/50 dark:border-gray-700 shadow-sm backdrop-blur-sm">
                  <span className="w-6 h-6 bg-amber-400 rounded-full flex items-center justify-center text-[10px] text-indigo-900 font-black shadow-lg">!</span>
                  <span className="text-sm font-bold text-slate-700 dark:text-gray-200">Interview Integrity Checks Active</span>
                </div>
              </div>
            </div>
          </div>

          <div className="p-8 md:p-16 flex flex-col justify-center relative bg-white dark:bg-gray-900 transition-colors">
            <h2 className="text-3xl font-black text-slate-900 dark:text-white mb-8 transition-colors">Candidate Login</h2>
            <div className="space-y-6 text-gray-900 dark:text-gray-100">
              <div>
                <label className="block text-[10px] font-bold text-slate-400 dark:text-gray-500 uppercase tracking-widest mb-2 ml-1">Student ID / Roll No</label>
                <input type="text" autoComplete="off" className="w-full px-6 py-4 bg-white dark:bg-gray-800 border-2 border-slate-100 dark:border-gray-700 rounded-2xl focus:border-indigo-500 dark:focus:border-indigo-500 outline-none text-slate-900 dark:text-white placeholder-slate-300 dark:placeholder-gray-600 transition-all shadow-sm focus:shadow-md" placeholder="Enter your ID" value={studentId} onChange={(e) => { setStudentId(e.target.value); setErrors({ ...errors, studentId: '' }); }} />
                {errors.studentId && <p className="text-red-500 text-[10px] mt-2 font-bold ml-1">{errors.studentId}</p>}
              </div>
              <div className={isLinkAutomated ? "hidden" : ""}>
                <label className="block text-[10px] font-bold text-slate-400 dark:text-gray-500 uppercase tracking-widest mb-2 ml-1">Institution</label>
                <select className="w-full px-6 py-4 bg-white dark:bg-gray-800 border-2 border-slate-100 dark:border-gray-700 rounded-2xl focus:border-indigo-500 dark:focus:border-indigo-500 outline-none appearance-none text-slate-900 dark:text-white transition-all shadow-sm focus:shadow-md" value={collegeId} onChange={(e) => { setCollegeId(e.target.value); setErrors({ ...errors, college: '' }); }}>
                  <option value="" className="text-gray-400">Select Institution...</option>
                  {colleges.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {errors.college && <p className="text-red-500 text-[10px] mt-2 font-bold ml-1">{errors.college}</p>}
              </div>
              <div className={isLinkAutomated ? "hidden" : ""}>
                <label className="block text-[10px] font-bold text-slate-400 dark:text-gray-500 uppercase tracking-widest mb-2 ml-1">Test Selection</label>
                <select className="w-full px-6 py-4 bg-white dark:bg-gray-800 border-2 border-slate-100 dark:border-gray-700 rounded-2xl focus:border-indigo-500 dark:focus:border-indigo-500 outline-none appearance-none text-slate-900 dark:text-white transition-all shadow-sm focus:shadow-md" value={selectedAssessmentId} onChange={(e) => { setSelectedAssessmentId(e.target.value); setErrors({ ...errors, assessment: '' }); }}>
                  <option value="" className="text-gray-400">Choose Assessment...</option>
                  {assessments.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                {errors.assessment && <p className="text-red-500 text-[10px] mt-2 font-bold ml-1">{errors.assessment}</p>}
              </div>
              <button onClick={handleStartAssessment} className="w-full bg-indigo-600 text-white py-5 rounded-[1.5rem] font-bold text-xl shadow-xl shadow-indigo-100 dark:shadow-none hover:bg-indigo-700 active:scale-[0.98] transition mt-4 hover:shadow-2xl">Begin Test</button>

            </div>

            {showAdminLogin && (
              <div className="absolute inset-0 bg-white/98 dark:bg-gray-950/98 backdrop-blur-md z-50 flex flex-col items-center justify-center p-12 transition-all">
                <button onClick={() => { setShowAdminLogin(false); setView('LANDING'); window.history.pushState({}, '', '/'); }} className="absolute top-8 right-8 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"><svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" /></svg></button>
                <div className="w-full max-w-xs space-y-6 text-center">
                  <h3 className="text-2xl font-black text-gray-900 dark:text-white uppercase transition-colors">Recruiter Portal</h3>
                  <input type="password" autoComplete="new-password" placeholder="Access PIN" className="w-full px-6 py-4 bg-gray-100 dark:bg-gray-800 border-2 border-transparent rounded-2xl text-center text-3xl font-black tracking-widest focus:border-indigo-500 outline-none text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 transition-all" value={adminPin} onChange={(e) => setAdminPin(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAdminLogin()} autoFocus />
                  {loginError && <p className="text-red-600 text-[10px] font-bold uppercase animate-bounce">{loginError}</p>}
                  <button onClick={handleAdminLogin} className="w-full bg-gray-900 dark:bg-white dark:text-gray-900 text-white py-4 rounded-2xl font-bold shadow-2xl transition-all hover:scale-105">Verify identity</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {renderAlert()}
    </Layout>
  );
};

export default App;
